import React, { useContext } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap } from '@atlaskit/css';
import { Anchor, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useUserProfileLink } from '../../hooks/useUserProfileLink';
import { UserProfileCardContext } from '../../store/context';
import type { PersonProfileData } from '../../types';

import { useProfileCardSlider } from './ProfileCardSliderContext';
import { ViewReportingLineButton } from './ViewReportingLineButton';

const messages = defineMessages({
	managerHeading: {
		id: 'user-profile-card.ManagerSection.managerHeading',
		defaultMessage: 'Manager',
		description: 'Section title for the manager row on the user profile card',
	},
});

const styles = cssMap({
	link: {
		display: 'block',
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		borderRadius: token('radius.medium'),
		color: token('color.text'),
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			textDecoration: 'none',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

interface ManagerSectionProps {
	manager: NonNullable<PersonProfileData['manager']>;
}

export function ManagerSection({ manager }: ManagerSectionProps): React.JSX.Element | null {
	const intl = useIntl();
	const { getTestId, cloudId, townsquareApp } = useContext(UserProfileCardContext);
	const { id, name, avatarUrl, jobTitle } = manager;
	const { href, target, onNavigate } = useUserProfileLink(id, cloudId, townsquareApp);
	const slider = useProfileCardSlider();
	const showOrgChartTrigger = Boolean(slider);

	if (!name) {
		return null;
	}

	return (
		<Stack space="space.075" testId={getTestId('manager')}>
			<Inline spread="space-between" alignBlock="start">
				<Text as="p" weight="medium" color="color.text">
					{intl.formatMessage(messages.managerHeading)}
				</Text>
				{showOrgChartTrigger && slider && (
					<ViewReportingLineButton
						onClick={slider.onView}
						controls={slider.backPaneId}
						testId={getTestId('view-reporting-line')}
					/>
				)}
			</Inline>
			<Anchor
				href={href}
				target={target}
				onClick={onNavigate}
				xcss={styles.link}
				testId={getTestId('manager-link')}
			>
				<Inline space="space.100" alignBlock="center">
					<Avatar src={avatarUrl ?? undefined} name={name} size="small" />
					<Stack>
						<Text size="small" maxLines={1}>
							{name}
						</Text>
						{jobTitle && (
							<Text size="small" color="color.text.subtlest" maxLines={1}>
								{jobTitle}
							</Text>
						)}
					</Stack>
				</Inline>
			</Anchor>
		</Stack>
	);
}
